import {isFeatureEnabled} from 'feature-flags';
import {FlagProps, Parameter, Parameters} from 'platform/components';
import {
  ThemeIconKey,
  Box,
  Heading,
  Hide,
  HStack,
  Link,
  Show,
  Space,
  Text,
  VStack,
} from 'platform/foundation';

import {HTMLAttributeAnchorTarget, isValidElement, ReactElement} from 'react';

import {isNil} from 'ramda';
import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {ActionParameters} from '../../ActionParameters/ActionParameters';
import {DynamicFlagList} from './DynamicFlagList';
import {SkeletonLoadingBox} from './SkeletonLoadingBox';
import {SnippetPlaceholder, SnippetPlaceholderProps} from './SnippetPlaceholder';

export interface SnippetProps extends TestIdProps {
  label: string;
  title?: string | ReactElement;
  action?: ReactElement;
  href?: string;
  target?: HTMLAttributeAnchorTarget;
  flags?: FlagProps[] | null;
  icon?: ThemeIconKey;
  parameters?: Parameter[];
  placeholder?: SnippetPlaceholderProps;
  isDisabled?: boolean;
  onClick?: () => void;
  customerId?: string;
  selectedContactId?: string;
  resourceId?: EntityResourceIds;
  recordId?: string;
  vehicleId?: string | Nullish;
  isLoading?: boolean;
}

// Reserves room for the flag list (or its "+N more" button) when a long title shrinks
const FLAG_LIST_MIN_WIDTH = 16;

const FILL_STYLES = {height: '100%'};

// The desktop card has only a min height (14), so percentage heights do not resolve there and the
// content centres itself with its own min height: card height 14 minus padding 2 * 2
const DESKTOP_CONTENT_MIN_HEIGHT = 10;

const DISABLED_OPACITY = 0.4;

const isTitleString = (title: SnippetProps['title']): title is string => typeof title === 'string';

const isTitleReactElement = (title: SnippetProps['title']): title is ReactElement =>
  isValidElement(title);

export function EntityHeaderSnippet(props: SnippetProps) {
  const hasContent = isNotNilOrEmpty(props.title) || isNotNilOrEmpty(props.flags);
  const shouldShowActionParameters =
    props.label === i18n.t('entity.businessCase.labels.contactPerson') ||
    props.label === i18n.t('entity.customer.labels.customer');

  const isForVehicleHeaderV3 =
    props.resourceId === 'VEHICLE' && isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3);

  return (
    <VStack height="100%" data-testid={props['data-testid']}>
      <Text
        size="xSmall"
        color={isForVehicleHeaderV3 ? 'secondary' : undefined}
        data-testid={suffixTestId('label', props)}
      >
        {props.label}
      </Text>

      <Space vertical={1} />

      <Box
        borderRadius="small"
        backgroundColor={[
          'palettes.neutral.20.100',
          'palettes.neutral.20.100',
          'palettes.neutral.10.100',
        ]}
        opacity={props.isDisabled ? DISABLED_OPACITY : 1}
        minHeight={[20, 20, 14]}
        flex={[1, 1, undefined]}
        position="relative"
      >
        <SkeletonLoadingBox
          isLoading={props.isLoading}
          borderRadius="small"
          contentStyles={FILL_STYLES}
        >
          <Show when={hasContent}>
            <Box width="100%" height={['100%', '100%', undefined]} padding={[4, 4, 2]}>
              <VStack
                width="100%"
                height={['100%', '100%', undefined]}
                minHeight={[undefined, undefined, DESKTOP_CONTENT_MIN_HEIGHT]}
                justify={isNil(props.title) ? 'flex-start' : 'center'}
                spacing={1}
              >
                <HStack spacing={2} align="center">
                  <Show when={props.title}>
                    <Show when={isTitleReactElement(props.title)}>{props.title}</Show>

                    <Show when={isTitleString(props.title) ? props.title : null}>
                      {(title) =>
                        isNotNil(props.onClick) || isNotNilOrEmpty(props.href) ? (
                          <Box minWidth={0}>
                            <Link
                              size="small"
                              noWrap
                              href={props.href}
                              title={title}
                              onClick={props.onClick}
                              isDisabled={props.isDisabled}
                              leftIcon={props.icon}
                              data-testid={suffixTestId(`link`, props)}
                              target={props.target}
                            />
                          </Box>
                        ) : (
                          <Heading
                            size={5}
                            headingLevel={6}
                            isSingleLine
                            data-testid={suffixTestId(`heading`, props)}
                          >
                            {title}
                          </Heading>
                        )
                      }
                    </Show>
                  </Show>

                  <Show when={props.action}>
                    <Box flexShrink={0}>{props.action}</Box>
                  </Show>

                  <Show when={isNotNilOrEmpty(props.flags)}>
                    <Box flex={1} minWidth={FLAG_LIST_MIN_WIDTH}>
                      <DynamicFlagList data={props.flags ?? []} />
                    </Box>
                  </Show>
                </HStack>
                <Show when={shouldShowActionParameters}>
                  <ActionParameters
                    parameters={props.parameters}
                    customerId={props.customerId}
                    color="secondary"
                    tooltipLabelSms={i18n.t('general.actions.sendMessage')}
                    tooltipLabelEmail={i18n.t('general.actions.sendEmail')}
                    resourceId={props.resourceId}
                    recordId={props.recordId}
                    vehicleId={props.vehicleId}
                  />
                </Show>
                <Hide when={shouldShowActionParameters}>
                  <Parameters
                    color="secondary"
                    size="xSmall"
                    parameters={props.parameters}
                    data-testid={suffixTestId(`parameters`, props)}
                  />
                </Hide>
              </VStack>
            </Box>
          </Show>

          <Hide when={hasContent}>
            <SnippetPlaceholder
              isDisabled={props.isDisabled}
              {...props.placeholder}
              data-testid={suffixTestId(`placeholder`, props)}
            />
          </Hide>
        </SkeletonLoadingBox>
      </Box>
    </VStack>
  );
}
